import { Link, Head } from '@inertiajs/react'
import Layout from '../../components/Layout'

interface TeamItem {
  id: number
  name: string
  description: string | null
  is_private: boolean
  member_count: number
  agenda_count: number
  current_user_role: string | null
}

interface Props {
  teams: TeamItem[]
  can_create: boolean
}

export default function TeamsIndex({ teams, can_create }: Props) {
  return (
    <Layout>
      <Head title="Teams — Pinnacle" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text-primary font-heading">Teams</h1>
          <p className="text-base text-text-secondary">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
        </div>
        {can_create && (
          <Link
            href="/teams/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-base font-semibold text-white bg-pinnacle-red rounded hover:bg-pinnacle-red-dark transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Team
          </Link>
        )}
      </div>

      {teams.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone p-12 text-center">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-text-primary/20 mb-3">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <p className="text-text-muted text-lg">No teams yet</p>
          {can_create && (
            <Link href="/teams/new" className="mt-3 inline-block text-base text-text-primary hover:underline">
              Create your first team
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <Link
              key={team.id}
              href={`/teams/${team.id}`}
              className="bg-white rounded-lg border border-stone p-6 hover:shadow-md transition-shadow group"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-xl font-semibold text-text-primary font-heading transition-colors leading-tight">
                  {team.name}
                </h3>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  {team.is_private && (
                    <span className="text-sm font-semibold text-text-muted bg-stone px-2 py-0.5 rounded-full">
                      Private
                    </span>
                  )}
                  {team.current_user_role && (
                    <span className="text-sm font-semibold text-text-primary bg-charcoal/5 px-2 py-0.5 rounded-full capitalize">
                      {team.current_user_role}
                    </span>
                  )}
                </div>
              </div>

              {team.description && (
                <p className="text-base text-text-secondary mt-1 line-clamp-2">{team.description}</p>
              )}

              <div className="flex items-center gap-4 mt-4 text-text-muted text-base">
                <div className="flex items-center gap-1.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>{team.member_count} member{team.member_count !== 1 ? 's' : ''}</span>
                </div>
                {team.agenda_count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    <span>{team.agenda_count} agenda{team.agenda_count !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  )
}
